import { Divider, Stack, Typography } from "@mui/material";
import { Img } from "../Img";
import { formatImgUrl } from "utils/helpers";
import { forwardRef, useMemo, useRef, Fragment } from "react";
import { Link, SmTextSemibold, StyledRouterA, XsText, XsTextMd } from "../Text";
import CountryFlag from "../CountryFlag";
import { useIntl } from "react-intl";
import { Swiper } from "swiper/react";
import { SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";
import defaultImage from "Assets/images/default_img.webp";
import Row from "../Row";
import { StyledSupplier } from "./Supplier.styles";
import { CompanyItemType, SupplierType } from "types/supplier";
import { DIRECT_PATHS } from "constants/routes";
import { PlanChip } from "../Plan";
import ListingChip from "../chips/ListingChip";
import { followRel } from "helpers/format";
import { RatingInfors } from "Components/infos";
import SupplierVerfifyTooltip from "Components/tooltip/SupplierVerifyTooltip";
import { COLORS } from "constants/layout";
import TimeDiff from "Components/TimeDiff";
import dayjs from "dayjs";
import SendInquiryButton from "Components/button/SendInquiryButton";
import { SendMessageButton } from "Components/button/index";
import useBreakpoints from "hooks/useBreakpoint";
import { SUPPLIER_PLANS } from "constants/data";
import UserAvatar from "Components/avatar/UserAvatar";
import { isAdvanceAndStarter, isNotFreeAndUnverified } from "helpers/plan";
import { PlanDataType } from "types/common";
import { useTranslation } from "hooks/useTranslation";
import { formatCurrencyWithUnit } from "helpers/string";

type Props = {
  company?: SupplierType;
  fullMode?: boolean;
  isPageDetail?: boolean;
  isNotShowButton?: boolean;
};

const Supplier = forwardRef(({ company, fullMode = true, isPageDetail= false , isNotShowButton=false}: Props, ref: any) => {
  const { isMobile } = useBreakpoints();
  const intl = useIntl();
  const NOW = dayjs();
  const {
    farm_name,
    fd_country,
    country_code,
    slug = "",
    farm_avatar,
    gallery_preview = [],
    plan_name,
    business_types,
    short_description,
    export_countries,
    farm_items = [],
    export_categories = [],
    activity_tags = [],
    no_follow = 0,
    is_biz_verified,
    is_verified_work_email,
    trust_star,
    count_review,
    farm_id,
    last_updated = "",
  } = company || {};

  const swiperRef = useRef<any>(null);

  const handleSlideChange = (swiper: any) => {
    swiperRef.current.swiper.allowTouchMove =
      swiper.isBeginning && swiper.isEnd;
  };

  const linear = SUPPLIER_PLANS.find(
    (plan) => plan.label === plan_name
  )?.linear;

  const showRealMedia = useMemo(
    () => gallery_preview && gallery_preview.length > 0,
    [gallery_preview]
  );

  const medias = useMemo(
    () =>
      showRealMedia ? gallery_preview.filter((img) => img) : [defaultImage],
    [showRealMedia]
  );

  // const isValidPlan = isNotFreePlan({ plan_name } as PlanDataType);
  const isValidPlan = isNotFreeAndUnverified({plan_name} as PlanDataType);

  const isShorterMode = useMemo(
    () => isMobile || !fullMode,
    [isMobile, fullMode]
  );

  return (
    <StyledSupplier ref={ref} gradient={linear}>
      {/* Head informations */}
      <Stack direction="row" justifyContent="space-between">
        {SUPPLIER_PLANS.find((plan) => plan.label === plan_name)?.label2 ? (
          <PlanChip plan={plan_name} />
        ) : (
          <div style={{ width: 48 }} />
        )}
        <SupplierVerfifyTooltip
          data={{ is_biz_verified, is_verified_work_email }}
        />
        <XsText color={COLORS.gray[500]} className="time-text" minWidth={64}>
          {last_updated && (
            <TimeDiff startTime={last_updated} endTime={NOW.format()} />
          )}
        </XsText>
      </Stack>

      <Divider sx={{ mt: 0.5, mb: 1 }} />

      <Stack
        className="info_box"
        flex={1}
        direction={{ xs: "column", md: fullMode ? "row" : "column" }}
        spacing={1}
      >
        <Stack
          flex={1}
          className="left-box"
          maxWidth={isShorterMode ? "100%" : (isPageDetail? (isNotShowButton? "100%": "80%") :"50%")}
        >
          <Stack flex={1}>
            <Row spacing={1}>
              {farm_avatar && (
                <Link to={`/${slug}`} rel={followRel(no_follow)}>
                  <Img
                    src={formatImgUrl(farm_avatar, 3)}
                    alt="img"
                    className="image"
                  />
                </Link>
              )}
              <Stack spacing={0.5}>
                <StyledRouterA to={`/${slug}`} rel={followRel(no_follow)}>
                  <SmTextSemibold
                    className="cutoff-text oneline"
                    component="h2"
                  >
                    {farm_name}
                  </SmTextSemibold>
                </StyledRouterA>

                <Row spacing={1}>
                  {/* Rating infors */}
                  <RatingInfors
                    data={{ trust_star, count_review, name: farm_id }}
                  />
                  <CountryFlag
                    {...{ fd_country, country_code }}
                    component="h4"
                  />
                </Row>
              </Stack>
            </Row>

            <ListingChip tags={activity_tags} mt={1} mb={1.5} />

            {/* Main supplier informations  */}
            <Stack spacing={0.5}>
              {short_description && (
                <XsTextMd
                  className={`cutoff-text ${
                    isValidPlan ? "twoline" : "oneline"
                  }`}
                >
                  {short_description}
                </XsTextMd>
              )}
              {export_categories.length > 0 && (
                <XsText
                  color="var(--green-600)"
                  className="categories-text"
                  component="h4"
                >
                  {intl.formatMessage({ id: "supplier_of" })}:{" "}
                  {export_categories.map((category, i) => (
                    <StyledRouterA
                      to={DIRECT_PATHS.categorySupplier(category.name)}
                      key={i}
                    >
                      {category.category_name}
                      {i === export_categories.length - 1 ? "" : ", "}
                    </StyledRouterA>
                  ))}
                </XsText>
              )}
              {business_types && (
                <XsText className="ellipsis-text categories" component="h4">
                  {intl.formatMessage({ id: "business_types" }) + ": "}
                  <span>{business_types}</span>
                </XsText>
              )}
              {export_countries && (
                <XsText className="ellipsis-text categories" component="h4">
                  {intl.formatMessage({ id: "export_market" }) + ": "}
                  <span>{export_countries}</span>
                </XsText>
              )}
            </Stack>
          </Stack>

          {/* Owner informations */}
          {!isShorterMode && <OwnerInformations company={company} isNotShowButton={isNotShowButton}/>}
        </Stack>

        {/* Supplier's items and media */}
        {!isNotShowButton && <Stack
          direction="row"
          gap={1}
          className="right-box"
          width={isShorterMode ? "100%" : (isPageDetail? "calc(20% - 8px)" :"calc(50% - 8px)")}
        >
          {/* Items in mobile or not full Mode */}
          <Items {...{ farm_items, isValidPlan, isShorterMode }} isPageDetail={isPageDetail}/>

          {!isPageDetail && !isShorterMode && medias.length > 0 && isValidPlan && (
            <Link to={`/${slug}`} rel={followRel(no_follow)}>
              <Swiper
                onSlideChange={handleSlideChange}
                className="medias"
                modules={[Pagination]}
                pagination={{
                  clickable: true,
                }}
              >
                {medias.map((media, i) => (
                  <SwiperSlide key={i}>
                    <Img
                      src={showRealMedia ? formatImgUrl(media, 3) : media}
                      alt="media"
                      height="100%"
                      width="100%"
                      style={{ objectFit: "cover" }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Link>
          )}
        </Stack>}

        {/* Owner informations */}
        {isMobile && fullMode && <OwnerInformations company={company} isNotShowButton={isNotShowButton}/>}
      </Stack>
    </StyledSupplier>
  );
});

const Items = ({
  farm_items,
  isValidPlan,
  isShorterMode,
  isPageDetail
}: {
  isValidPlan: boolean;
  farm_items: CompanyItemType[];
  isShorterMode: boolean;
  isPageDetail?: boolean;
}) => {
  return (
    <Stack
      direction="row"
      gap={1}
      className="products"
      alignItems="center"
      width={isPageDetail? "100%" :`calc(100% - ${!isShorterMode && isValidPlan ? 250 : 0}px)`}
      justifyContent={isShorterMode ? "flex-start" : "flex-end"}
    >
      {farm_items
        .slice(0, isPageDetail ? 1: (isValidPlan ? (isShorterMode ? 3 :  2) : 3))
        .map((item, i) => (
          <Item key={i} {...{ isValidPlan, isShorterMode, item }} isPageDetail={isPageDetail}/>
        ))}
    </Stack>
  );
};

/* Owner informations */
const OwnerInformations = ({ company, isNotShowButton }: { company?: SupplierType, isNotShowButton?: boolean }) => {
  const { user_image, farmer_name, salutation, position, identity, farm_id, slug } =
    company || {};
  return (
    <Row spacing={{ xs: 1, md: 2 }} className="owner-info" mt={1.5}>
      <Row spacing={1} flex={1} overflow="hidden" className="left-box">
        <UserAvatar data={{ user_image, salutation }} />
        <Stack maxWidth="calc(100% - 48px)">
          <XsTextMd color={COLORS.gray[800]} className="ellipsis-text">
            {salutation ? `${salutation}. ` : ""}
            {farmer_name}
          </XsTextMd>
          <XsText color={COLORS.gray[600]} className="ellipsis-text">
            {position}
          </XsText>
        </Stack>
      </Row>

      {!isNotShowButton && <Row
        spacing={1}
        className="buttons-box"
        flex={1}
        whiteSpace="nowrap"
        justifyContent="flex-end"
      >
        <SendInquiryButton data={{ farm_id , slug}} isFromSupplier/>
        <SendMessageButton identity={identity} />
      </Row>}
    </Row>
  );
};

const Item = ({
  item,
  isValidPlan,
  isShorterMode,
  isPageDetail,
}: {
  item: CompanyItemType;
  isValidPlan: boolean;
  isShorterMode: boolean;
  isPageDetail?: boolean;
}) => {
  const t = useTranslation();
  const { farm_item_name, farm_item, moq, moq_unit, price_min, price_max } =
    item;

  return (
    <Stack
      height={isValidPlan ? 240 : "fit-content"}
      className={"item-box " + (isShorterMode ? "shorter" : "")}
      width={isPageDetail? "100%" : "calc(50% - 4px)"}
    >
      {isValidPlan && (
        <Link
          to={DIRECT_PATHS.itemDetail(farm_item)}
          className="item-img"
          style={{
            height: isShorterMode ? 114 : 156,
          }}
        >
          <Img src={formatImgUrl(item.farm_item_image, 2)} alt="img" />
        </Link>
      )}
      <Stack spacing={0.5} p={1}>
        <StyledRouterA to={DIRECT_PATHS.itemDetail(farm_item)}>
          <XsTextMd
            fontSize={10}
            height={isShorterMode ? 15 : 30}
            className={"cutoff-text " + (isShorterMode ? "oneline" : "twoline")}
            component="h3"
          >
            {farm_item_name}
          </XsTextMd>
        </StyledRouterA>

        {!isShorterMode && (
          <Fragment>
            <XsTextMd className="price ellipsis-text">
              {price_min <= 0 && t("to_be_negotiated")}
              {price_min > 0 &&
                `${formatCurrencyWithUnit(price_min)} ${
                  price_min < price_max
                    ? ` - ${formatCurrencyWithUnit(price_max)}`
                    : ""
                }`}
            </XsTextMd>

            {moq && (
              <Typography
                className="min_order_qlt ellipsis-text"
                color="#808089"
              >
                {t("min_order")}:{" "}
                <span>
                  {moq} {moq_unit}
                </span>
              </Typography>
            )}
          </Fragment>
        )}
      </Stack>
    </Stack>
  );
};

export default Supplier;
